'use client';
import React from 'react';
import { usePathname } from 'next/navigation';
import { emitAuthSuccess } from '@benzinga/auth-ui';
import { AuthenticationManager, GoogleOneTapLogin } from '@benzinga/session';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useIsUserLoggedIn, wallGate } from '@benzinga/user-context';

const AUTH_PATHS = ['/login', '/logout', '/register', '/welcome', '/account'];
const TOOL_PATHS = [
  '/applications',
  '/edge',
  '/portfolio',
  '/profile/portfolio',
  '/quote',
  '/reports',
  '/screener',
  '/sec',
];
const EXCLUDED_PATHS = [...AUTH_PATHS, ...TOOL_PATHS];

export const WALL_DECISION_TIMEOUT_MS = 10_000;

export const isOneTapExcludedPath = (pathname: string | null): boolean =>
  !!pathname && EXCLUDED_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`));

const whenPageIsIdle = (callback: () => void): (() => void) => {
  let idleId: number | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const schedule = () => {
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(callback, { timeout: 5000 });
    } else {
      timeoutId = setTimeout(callback, 0);
    }
  };

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }

  return () => {
    window.removeEventListener('load', schedule);
    if (idleId !== undefined) window.cancelIdleCallback(idleId);
    clearTimeout(timeoutId);
  };
};

export const GoogleOneTap: React.FC<{ embeddedWidget?: boolean }> = ({ embeddedWidget }) => {
  const session = React.useContext(SessionContext);
  const pathname = usePathname();
  const isLoggedIn = useIsUserLoggedIn();

  React.useEffect(() => {
    if (embeddedWidget || isLoggedIn || isOneTapExcludedPath(pathname)) return;

    const authManager = session.getManager(AuthenticationManager);
    let isActive = true;
    let isPageIdle = false;
    let isWallDecisionTimedOut = false;
    let isRequested = false;
    let hadWall = false;
    let wallDecisionTimeout: ReturnType<typeof setTimeout> | undefined;

    const onSignIn = async ({ auth, isNewUser }: GoogleOneTapLogin) => {
      const action = isNewUser ? 'register' : 'login';
      const tracking = session.getManager(TrackingManager);
      await tracking.setUser(auth.user);
      await tracking.trackAuthEvent(action, { auth_type: 'google-one-tap', is_paywalled: false, is_regwalled: false });
      emitAuthSuccess({ action, isNewUser, method: 'google-one-tap', surface: 'page', userId: String(auth.user.id) });
    };

    const showWhenNoWall = async () => {
      if (!isActive || !isPageIdle || isRequested || hadWall) return;
      const wallState = wallGate.getState();
      if (wallState === 'wall') {
        hadWall = true;
        return;
      }
      if (wallState === 'pending' && !isWallDecisionTimedOut) return;

      isRequested = true;
      await authManager.getAuthSession();
      if (!isActive || authManager.isLoggedIn()) return;
      if (wallGate.getState() === 'wall') {
        hadWall = true;
        return;
      }
      authManager.showGoogleOneTap(onSignIn);
    };

    const unsubscribe = wallGate.subscribe(state => {
      if (state === 'wall') {
        hadWall = true;
        if (isRequested) authManager.cancelGoogleOneTap();
        return;
      }
      showWhenNoWall();
    });

    const cancelIdle = whenPageIsIdle(() => {
      isPageIdle = true;
      wallDecisionTimeout = setTimeout(() => {
        isWallDecisionTimedOut = true;
        showWhenNoWall();
      }, WALL_DECISION_TIMEOUT_MS);
      showWhenNoWall();
    });

    return () => {
      isActive = false;
      unsubscribe();
      cancelIdle();
      clearTimeout(wallDecisionTimeout);
      if (isRequested) authManager.cancelGoogleOneTap();
    };
  }, [embeddedWidget, isLoggedIn, pathname, session]);

  return null;
};
